import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from '@angular/router';
import {
  faCaretDown,
  faCaretUp,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import {
  AaNgModule,
  DisplayStrategy,
  DropdownComponent,
  ISearchQuery,
  isNullOrWhiteSpace,
  SearchType,
} from 'aa-ng';
import { BehaviorSubject, combineLatest, map, tap } from 'rxjs';

import {
  IInstitutionContext,
  ISelectableUnit,
  NavigationTier,
  PageEnum,
  SelectableUnitGroup,
} from '../models';
import {
  AddSearchTermService,
  AlphabetFilterComponent,
  BaseDirective,
  DashboardSearchComponent,
  DashboardService,
  DialogScrollService,
  InstitutionsService,
} from '../shared';
import { sortByStringProperty } from '../shared/helpers/sort-by-string-property';

@Component({
  selector: 'app-dashboard-header',
  imports: [
    AaNgModule,
    AlphabetFilterComponent,
    AsyncPipe,
    DashboardSearchComponent,
    NgClass,
    RouterLink,
    RouterModule,
  ],
  templateUrl: './dashboard-header.component.html',
})
export class DashboardHeaderComponent
  extends BaseDirective
  implements OnDestroy
{
  public dashboardService = inject(DashboardService);
  private router = inject(Router);
  private institutionsService = inject(InstitutionsService);
  private route = inject(ActivatedRoute);
  private addSearchTermService = inject(AddSearchTermService);
  private dialogScrollService = inject(DialogScrollService);

  logoAltText: string;
  institutionSiteName: string;
  contactEmail: string;
  contactUrl: string;
  showInstitutionNameOnTabs: boolean;
  hideDashboardUnitDropdown: boolean;
  logoPath: string;
  context: IInstitutionContext;
  searchQuery: ISearchQuery;
  interval: ReturnType<typeof setInterval>;
  NavigationTier: typeof NavigationTier;
  expanded = false;
  blockHeight = 0;
  megaMenuHeight = 0;
  _lastScrollY = 0;
  homeUrl = '/';
  faCaretUp = faCaretUp;
  faCaretDown = faCaretDown;
  faSearch = faSearch;
  dontResetNextExpand = false;
  dialogOpen = false;
  toggleMenuText = '';
  displayAlphabetFilterForUnits = false;
  dashboardUnitTypesToExcludeFinalS: number[] = [];
  private previousPageGroup: PageEnum;
  private inputText: string;
  private _unitsExpanded$ = new BehaviorSubject<boolean>(false);
  private _displayExpansionText$ = new BehaviorSubject<boolean>(true);
  private _academicUnitsText$ = new BehaviorSubject<string>('');

  get unitsExpanded() {
    return this._unitsExpanded$.value;
  }
  @ViewChild('unitselector') unitSelectorElement: DropdownComponent;
  @ViewChild('header') headerElement: ElementRef;
  @ViewChild('unitmenu') unitMenuElement: ElementRef;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.dontResetNextExpand) {
      this.expanded = false;
    } else {
      this.dontResetNextExpand = false;
    }
    if (!this.dialogOpen) {
      this._lastScrollY = window.pageYOffset;
    }
  }

  constructor() {
    super();
    this.NavigationTier = NavigationTier;
    let blockHeight = 0;
    let megaMenuHeight = 0;
    // Hate to dirty check but this bypasses that spamming "value changed after checked" angular error
    this.interval = setInterval(() => {
      if (this.dialogOpen) {
        return;
      }
      if (
        this.headerElement &&
        this.headerElement.nativeElement &&
        this.headerElement.nativeElement.offsetHeight
      ) {
        blockHeight = this.headerElement.nativeElement.offsetHeight;

        if (this.headerElement.nativeElement.className.includes('collapse')) {
          this.blockHeight = 200;
        } else {
          this.blockHeight = blockHeight;
          this.dontResetNextExpand = true;
        }
      }
      if (
        this.unitMenuElement &&
        this.unitMenuElement.nativeElement &&
        this.unitMenuElement.nativeElement.offsetHeight
      ) {
        blockHeight -= this.unitMenuElement.nativeElement.offsetHeight;
      }
      if (window.innerHeight) {
        megaMenuHeight = window.innerHeight - blockHeight - 72;
        if (megaMenuHeight < 200) {
          megaMenuHeight = 200;
        }
        if (this.megaMenuHeight !== megaMenuHeight) {
          this.megaMenuHeight = megaMenuHeight;
        }
      }
    }, 100);

    this._subscriptions.push(
      combineLatest([
        this.institutionsService.context,
        this.dashboardService.dashboardShortName$,
      ]).subscribe(([context, dashboardShortName]) => {
        this._displayExpansionText$.next(
          context.toggleShowAndHideForUnitDropdown,
        );
        if (
          context.unitDropdownDisplayText != null &&
          context.unitDropdownDisplayText !== ''
        ) {
          this._academicUnitsText$.next(context.unitDropdownDisplayText);
        } else {
          this._academicUnitsText$.next(
            dashboardShortName ?
              `${dashboardShortName} Academic Units`
            : 'Academic Units',
          );
        }
      }),
      this.route.data.subscribe(async (data) => {
        const context: IInstitutionContext = data['context'];

        this.contactEmail = context.contactEmail;
        this.contactUrl = context.contactUrl;
        this.showInstitutionNameOnTabs = context.showInstitutionNameOnTabs;
        this.hideDashboardUnitDropdown = context.hideDashboardUnitDropdown;
        this.displayAlphabetFilterForUnits =
          context.displayAlphabetFilterForUnits;
        this.dashboardUnitTypesToExcludeFinalS =
          context.dashboardUnitTypesToExcludeFinalS ?? [];
      }),
      this.route.queryParamMap.subscribe((params) => {
        const query = params.get('query');
        const searchType = params.get('searchType');

        // Only update searchQuery if query params are present
        if (query !== null || searchType !== null) {
          this.searchQuery = {
            query: query || null,
            type:
              searchType ?
                +SearchType[searchType as keyof typeof SearchType]
              : SearchType.Topic,
          };
          this.inputText = this.searchQuery.query;
        }
      }),
      this.dashboardService.unitNavigationEvent.subscribe(() => {
        if (this.unitSelectorElement) {
          this.unitSelectorElement.openMenu = false;
        }
        this._unitsExpanded$.next(false);
      }),
      this.dashboardService.pageGroup.subscribe((pageGroup) => {
        if (
          this.previousPageGroup &&
          this.previousPageGroup !== pageGroup &&
          this.previousPageGroup === PageEnum.SearchResults
        ) {
          this.searchQuery = {
            query: '',
            type: this.searchQuery.type || SearchType.Topic,
          };
          this.inputText = this.searchQuery.query;
        }
        this.previousPageGroup = pageGroup;
      }),
      combineLatest([
        this.dashboardService.state,
        this.institutionsService.context,
      ]).subscribe(([, context]) => {
        this.context = context;
        this.logoAltText =
          (this.dashboardService.isSystem ?
            this.dashboardService.dashboardTitle
          : this.context.institutionName
          ).replace(', The', '') +
          ' - ' +
          this.dashboardService.dashboardSecondarySiteName;
      }),
      this.router.events.subscribe(async (evt) => {
        if (evt instanceof NavigationEnd) {
          this._unitsExpanded$.next(false);
        }
      }),
      this.addSearchTermService.newTerm.subscribe((newTerm) => {
        newTerm = newTerm.indexOf(' ') === -1 ? newTerm : `"${newTerm}"`;
        this.searchQuery = {
          query:
            isNullOrWhiteSpace(this.inputText) ? newTerm : (
              `${this.inputText} OR ${newTerm}`
            ),
          type: this.searchQuery.type,
        };
        this.inputText = this.searchQuery.query;
      }),
      this.dialogScrollService.dialogOpen.subscribe(
        (dialogOpen) => (this.dialogOpen = dialogOpen),
      ),
      combineLatest([
        this.dashboardService.params,
        this.dashboardService.institutions,
      ])
        .pipe(
          map(([params, institutions]) => {
            if (this.dashboardService.isSystem) {
              this.dashboardService.displayUnits.next(institutions);

              if (!params.instId) {
                this.dashboardService.selectedDisplayUnit.next({
                  id: undefined,
                  name: 'All Schools',
                  tier: NavigationTier.System,
                });
              }
            }
          }),
        )
        .subscribe(),
      combineLatest([
        this.institutionsService.context,
        this.dashboardService.params,
        this.dashboardService.institutions,
        this.dashboardService.units,
        this.dashboardService.unitTypes,
        this.dashboardService.selectedUnitType,
      ])
        .pipe(
          map(
            ([
              context,
              params,
              institutions,
              units,
              unitTypes,
              selectedUnitType,
            ]) => {
              let displayTree: SelectableUnitGroup[] = [];
              if (context.unitBlacklist && context.unitBlacklist.length) {
                units = units.filter(
                  (u) => !context.unitBlacklist.includes(u.id),
                );
              }

              if (
                (this.dashboardService.isSystem && !params.instId) ||
                context.useCombinedSystemDashboard
              ) {
                // Load system groups
                displayTree = institutions.map((x) => {
                  const children = units.filter(
                    (unit) => unit.parentId === x.id,
                  );

                  return new SelectableUnitGroup(
                    x,
                    unitTypes.map((type) => {
                      const includeFinalS =
                        !this.dashboardUnitTypesToExcludeFinalS.includes(
                          type.type,
                        );
                      return {
                        displayStrategy: new DisplayStrategy(
                          `${type.name}${includeFinalS ? 's' : ''}`,
                        ),
                        data: (unit: ISelectableUnit) =>
                          unit.type === type.type,
                        isCurrent: type === selectedUnitType,
                        isDisabled: !children.find((c) => c.type === type.type),
                      };
                    }),
                    children,
                  );
                });
              } else {
                // Load single inst group
                const children = units
                  .filter((unit) =>
                    this.dashboardService.isSystem ?
                      unit.parentId === params.instId
                    : true,
                  )
                  .sort(
                    sortByStringProperty((u) =>
                      (
                        context.showDashboardUnitCollaborationWidget &&
                        u.displayName
                      ) ?
                        u.displayName
                      : u.type === 2 || u.type === 3 ? u.shortName
                      : u.name,
                    ),
                  );
                if (
                  context.displayNameForAllDashboardUnitsInOneTab != null &&
                  context.displayNameForAllDashboardUnitsInOneTab !== ''
                ) {
                  // If the institution context has a value for displayNameForAllDashboardUnitsInOneTab, then we ignore unit types and just display all units in one tab with the provided name
                  displayTree = [
                    new SelectableUnitGroup(
                      null,
                      [
                        {
                          displayStrategy: new DisplayStrategy(
                            context.displayNameForAllDashboardUnitsInOneTab,
                          ),
                          data: () => true,
                          isCurrent: true,
                          isDisabled: false,
                        },
                      ],
                      children,
                    ),
                  ];
                } else {
                  displayTree = [
                    new SelectableUnitGroup(
                      null,
                      unitTypes.map((type) => {
                        const includeFinalS =
                          !this.dashboardUnitTypesToExcludeFinalS.includes(
                            type.type,
                          );
                        return {
                          displayStrategy: new DisplayStrategy(
                            `${type.name}${includeFinalS ? 's' : ''}`,
                          ),
                          data: (unit: ISelectableUnit) =>
                            unit.type === type.type,
                          isCurrent: type === selectedUnitType,
                          isDisabled: !children.find(
                            (c) => c.type === type.type,
                          ),
                        };
                      }),
                      children,
                    ),
                  ];
                }
              }

              this.dashboardService.displayUnitTree.next(displayTree);
            },
          ),
        )
        .subscribe(),
      combineLatest([
        this._academicUnitsText$,
        this._displayExpansionText$,
        this._unitsExpanded$,
      ])
        .pipe(
          tap(([academicUnitsText, displayExpansionText, unitsExpanded]) => {
            let toggleMenuText = '';
            if (displayExpansionText) {
              toggleMenuText = unitsExpanded ? 'Hide' : 'Show';
            }
            toggleMenuText += ` ${academicUnitsText}`;
            this.toggleMenuText = toggleMenuText;
          }),
        )
        .subscribe(),
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    clearInterval(this.interval);
  }

  toggleUnitMenu(): void {
    this._unitsExpanded$.next(!this._unitsExpanded$.value);
  }

  search(event: ISearchQuery): void {
    this.router.navigate(['search'], {
      queryParamsHandling: 'merge',
      queryParams: {
        query: event.query,
        searchType: SearchType[event.type],
        institutionIds: event.includedInstitutionIds,
      },
    });
  }

  trackText(event: string) {
    this.inputText = event;
  }

  filterUnits(filteredUnits: ISelectableUnit[]) {
    // Get the current group from the displayUnitTree
    const displayTree = this.dashboardService.displayUnitTree.value;
    if (displayTree && displayTree.length > 0) {
      // Check if the filtered list contains all units (no filter applied)
      const allUnits = displayTree.flatMap((group) => group._children);
      const isFilterActive = filteredUnits.length < allUnits.length;

      // Update each group with the alphabet filter
      displayTree.forEach((group) => {
        if (isFilterActive) {
          // Pass the filtered units to the group
          group.setAlphabetFilter(filteredUnits);
        } else {
          // Clear the alphabet filter (show all)
          group.setAlphabetFilter(null);
        }
      });
    }
  }
}
